import React, { useRef, useState, useCallback, useEffect } from 'react';
import { API_ENDPOINTS, getImageUrl } from '../config/api';

const RichContentEditor = ({ value, onChange, placeholder = "Start typing or paste images..." }) => {
  const editorRef = useRef(null);
  const [isUploading, setIsUploading] = useState(false);

  // Initialize content when value changes
  useEffect(() => {
    if (editorRef.current && value !== editorRef.current.innerHTML) {
      editorRef.current.innerHTML = value || '';
    }
  }, [value]);

  const handleInput = useCallback(() => {
    if (editorRef.current) {
      const html = editorRef.current.innerHTML;
      // Don't call onChange if the content is just empty or placeholder-like
      if (html !== '<br>' && html !== '<div><br></div>') {
        onChange(html);
      } else {
        onChange('');
      }
    }
  }, [onChange]);

  const uploadMediaFromBlob = useCallback(async (blob, isVideo = false) => {
    try {
      setIsUploading(true);
      const formData = new FormData();
      const extension = isVideo ? 'mp4' : 'png';
      const filename = `pasted-${isVideo ? 'video' : 'image'}-${Date.now()}.${extension}`;

      formData.append('media', blob, filename);

      const response = await fetch(`${API_ENDPOINTS.LESSONS}/upload-media`, {
        method: 'POST',
        body: formData
      });

      const result = await response.json();

      if (response.ok && result.files && result.files.length > 0) {
        const mediaUrl = getImageUrl(result.files[0].url);
        return { url: mediaUrl, type: isVideo ? 'video' : 'image' };
      } else {
        throw new Error(result.error || 'Upload failed');
      }
    } catch (error) {
      console.error('Media upload error:', error);
      alert(`Failed to upload pasted ${isVideo ? 'video' : 'image'}`);
      return null;
    } finally {
      setIsUploading(false);
    }
  }, []);

  const handlePaste = useCallback(async (e) => {
    const items = e.clipboardData?.items;
    if (!items) return;

    // Check if paste contains images or videos
    const imageItems = Array.from(items).filter(item => item.type.startsWith('image/'));
    const videoItems = Array.from(items).filter(item => item.type.startsWith('video/'));

    const hasMedia = imageItems.length > 0 || videoItems.length > 0;

    if (hasMedia) {
      e.preventDefault();

      // Handle images
      for (const item of imageItems) {
        const blob = item.getAsFile();
        if (blob) {
          const result = await uploadMediaFromBlob(blob, false);
          if (result) {
            // Insert image into editor
            const img = document.createElement('img');
            img.src = result.url;
            img.style.maxWidth = '100%';
            img.style.height = 'auto';
            img.style.margin = '10px 0';
            img.style.borderRadius = '8px';

            // Insert at cursor position or at end
            const selection = window.getSelection();
            if (selection.rangeCount > 0) {
              const range = selection.getRangeAt(0);
              range.insertNode(img);
              // Move cursor after the image
              range.setStartAfter(img);
              range.setEndAfter(img);
              selection.removeAllRanges();
              selection.addRange(range);
            } else {
              editorRef.current.appendChild(img);
            }
          }
        }
      }

      // Handle videos
      for (const item of videoItems) {
        const blob = item.getAsFile();
        if (blob) {
          const result = await uploadMediaFromBlob(blob, true);
          if (result) {
            // Insert video into editor
            const video = document.createElement('video');
            video.src = result.url;
            video.controls = true;
            video.style.maxWidth = '100%';
            video.style.height = 'auto';
            video.style.margin = '10px 0';
            video.style.borderRadius = '8px';

            // Insert at cursor position or at end
            const selection = window.getSelection();
            if (selection.rangeCount > 0) {
              const range = selection.getRangeAt(0);
              range.insertNode(video);
              // Move cursor after the video
              range.setStartAfter(video);
              range.setEndAfter(video);
              selection.removeAllRanges();
              selection.addRange(range);
            } else {
              editorRef.current.appendChild(video);
            }
          }
        }
      }

      // Trigger input event to update the value
      setTimeout(() => handleInput(), 0);
    }
  }, [uploadMediaFromBlob, handleInput]);

  const handleKeyDown = useCallback((e) => {
    // Handle Enter key to prevent default behavior in some cases
    if (e.key === 'Enter' && !e.shiftKey) {
      // Allow normal Enter behavior
    }
  }, []);

  return (
    <div className="relative">
      <div
        ref={editorRef}
        contentEditable
        className="w-full min-h-[120px] p-3 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white"
        onInput={handleInput}
        onPaste={handlePaste}
        onKeyDown={handleKeyDown}
        style={{
          lineHeight: '1.6',
          fontFamily: 'inherit',
          fontSize: '14px',
          wordWrap: 'break-word',
          overflowWrap: 'break-word'
        }}
      />
      {!value && (
        <div className="absolute top-3 left-3 text-gray-400 pointer-events-none">
          {placeholder}
        </div>
      )}
      {isUploading && (
        <div className="absolute top-2 right-2 bg-blue-500 text-white text-xs px-2 py-1 rounded">
          Uploading media...
        </div>
      )}
      <div className="mt-2 text-xs text-gray-500">
        Idea Tip: You can paste images and videos directly into the editor by copying them from anywhere!
      </div>
    </div>
  );
};

export default RichContentEditor;



